// Shared notifications domain client.
//
// Owns the notification endpoint paths and request shapes so web, mobile, and
// Guardian stop hand-copying `/api/notifications` strings. Transport, auth, and
// response unwrapping stay in each app via the adapter it passes in.
//
// The adapter mirrors the createApiClient method surface:
//   get(path, options)
//   post(path, body, options)
//   patch(path, body, options)
// Each app supplies an adapter that maps these onto its own transport.
import {
  readNotificationListResponse,
  readNotificationMarkAllResponse,
  readNotificationMutationResponse,
} from '../notification-responses.js';
import { readNotificationCreateRequest, readNotificationId, readNotificationListRequest } from '../notification-requests.js';
export { NotificationRequestContractError, readNotificationCreateRequest, readNotificationId, readNotificationListRequest } from '../notification-requests.js';

export const NOTIFICATION_PATHS = {
  list: '/api/notifications',
  create: '/api/notifications/create',
  markAllRead: '/api/notifications/mark-as-read',
};

export const buildMarkNotificationReadPath = (notificationId) =>
  `/api/notifications/${encodeURIComponent(notificationId)}/mark-as-read`;

export const buildMarkNotificationUnreadPath = (notificationId) =>
  `/api/notifications/${encodeURIComponent(notificationId)}/mark-as-unread`;

// Canonical body shape for POST /api/notifications/create. Keeping the key set
// in one place is the whole point: every app previously assembled this inline.
export const buildSendNotificationBody = ({
  type,
  actor,
  target,
  targetType,
  recipientCommunity,
  additionalData,
  scope,
} = {}) => ({
  type,
  actor,
  target,
  targetType,
  recipientCommunity,
  additionalData,
  scope,
});

export const createNotificationsClient = (adapter) => {
  if (!adapter || typeof adapter.get !== 'function' || typeof adapter.post !== 'function' || typeof adapter.patch !== 'function') {
    throw new TypeError('createNotificationsClient requires an adapter with get/post/patch methods.');
  }

  return {
    async send(notification, options = {}) {
      return readNotificationMutationResponse(
        await adapter.post(NOTIFICATION_PATHS.create, readNotificationCreateRequest(buildSendNotificationBody(notification)), options),
        'notification creation',
      );
    },

    async markRead(notificationId, options = {}) {
      return readNotificationMutationResponse(await adapter.patch(buildMarkNotificationReadPath(readNotificationId(notificationId)), null, options), 'mark notification read', true);
    },

    async markUnread(notificationId, options = {}) {
      return readNotificationMutationResponse(await adapter.patch(buildMarkNotificationUnreadPath(readNotificationId(notificationId)), null, options), 'mark notification unread', false);
    },

    async markAllRead(userId, options = {}) {
      return readNotificationMarkAllResponse(await adapter.patch(NOTIFICATION_PATHS.markAllRead, { userId }, options));
    },

    async list(params = {}, options = {}) {
      return readNotificationListResponse(await adapter.get(NOTIFICATION_PATHS.list, { ...options, params: readNotificationListRequest(params) }));
    },
  };
};
